<script setup lang="ts">
import { formatMegawattsCustom } from '@/util/formatters'
import { Icon } from '@iconify/vue'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeSeriesScale,
  Title,
  Tooltip,
  type ChartOptions,
} from 'chart.js'
import 'chartjs-adapter-luxon'
import Annotation from 'chartjs-plugin-annotation'
import { DateTime } from 'luxon'
import { reactive, ref, watch } from 'vue'
import { Chart } from 'vue-chartjs'
import { formatISODateToDateString } from '@/util/formatters'
import type { ILoadForecastFilters } from '@/types/cpm/filters'
import { ISOs } from '@/types/core/Site'

const RevolveTimezone = 'US/Central'
const emit = defineEmits(['filtersChange'])

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeSeriesScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Annotation,
  BarController,
  BarElement
)

// MISO issues each hour's forecast up to 5 days ahead. `0` is the most recently issued forecast.
const DaysAheadOptions = [
  { label: 'Latest', value: 0 },
  { label: 'Day-1', value: 1 },
  { label: 'Day-2', value: 2 },
  { label: 'Day-3', value: 3 },
  { label: 'Day-4', value: 4 },
  { label: 'Day-5', value: 5 },
]

interface IForecastData {
  timestamp: string
  expected_load_mwh: number
  actual_load_mwh: number
}

const props = defineProps<{
  loading: boolean
  initialStart: DateTime
  initialEnd: DateTime
  forecastData: IForecastData[]
  initialDaysAhead: number
}>()

const daterange = ref<[Date, Date]>([new Date(props.initialStart.toString()), new Date(props.initialEnd.toString())])

const chartData = reactive<any>({
  datasets: [
    {
      type: 'line',
      label: 'Expected Load',
      backgroundColor: '#108BFF',
      borderColor: '#108BFF',
      data: [],
      stepped: true,
      pointRadius: 3,
      order: 3,
      parsing: {
        xAxisKey: 'timestamp',
        yAxisKey: 'expected_load_mwh',
      },
    },
    {
      type: 'line',
      label: 'Actual Load',
      backgroundColor: '#339966',
      borderColor: '#339966',
      order: 2,
      pointRadius: 3,
      data: [],
      parsing: {
        xAxisKey: 'timestamp',
        yAxisKey: 'actual_load_mwh',
      },
    },
    {
      type: 'bar',
      label: 'Peak Hour',
      backgroundColor: '#f95b00',
      borderColor: '#f95b00',
      order: 1,
      pointRadius: 30,
      data: [],
      parsing: {
        xAxisKey: 'timestamp',
        yAxisKey: 'actual_load_mwh',
      },
    },
  ],
})

const chartOptions = reactive<ChartOptions<'line'>>({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'timeseries',
      time: {
        unit: 'hour',
        tooltipFormat: 'M/dd/yyyy HH:mm',
        displayFormats: {
          hour: 'M/dd/yyyy H:mm',
        },
      },
      adapters: {
        date: {
          setZone: true,
          zone: RevolveTimezone,
        },
      },
      ticks: {
        callback: (value) => {
          return formatDatetimeForChart(value as number)
        },
      },
    },
    y: {
      stacked: false,
      beginAtZero: false,
      ticks: {
        callback: (value) => {
          return `${formatMegawattsCustom(value as number, 0)} MWh`
        },
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        title: (ctx) => {
          return formatDatetimeForChart(ctx[0]?.parsed.x ?? 0)
        },
        label: (ctx) => {
          return `${ctx.dataset.label}: ${formatMegawattsCustom(ctx.parsed.y, 2)} MWh`
        },
      },
    },
    legend: {
      position: 'top',
      align: 'end',
    },
  },
})

watch(
  () => props.forecastData,
  () => {
    updateChart()
  }
)

const formatExpectedDataSet = (data: Array<any>) => {
  return data.map((item) => {
    return {
      timestamp: formatDatetime(item.timestamp),
      expected_load_mwh: item.expected_load_mwh,
    }
  })
}

const formatActualDataSet = (data: Array<any>) => {
  return data.map((item) => {
    return {
      timestamp: formatDatetime(item.timestamp),
      actual_load_mwh: item.actual_load_mwh !== '0.00' ? item.actual_load_mwh : null,
    }
  })
}

const formatPeakHourDataSet = (data: Array<any>) => {
  return data
    .filter((item) => item.is_peak_hour)
    .map((item) => {
      return {
        timestamp: item.timestamp,
        actual_load_mwh: item.actual_load_mwh,
      }
    })
}

const formatDatetime = (timestamp: string) => {
  return DateTime.fromISO(timestamp).toISO()
}

/**
  * Format the datetime for the chart
  * @param {number} timestamp
  * @returns {string}

  * @example
  * ```
  * formatDatetimeForChart(1630000000000)
  * // returns '8/27/2021 1:00'
  * ```
  *
  * @description
  * This function takes a timestamp and formats it to a string in the format "M/dd/yyyy H:mm".
  * But with one hour added to the hour value and making 24:00 possible value for time without changing a date.
 */
const formatDatetimeForChart = (timestamp: number) => {
  const formattedVal = new Date(new Date(timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago' }))

  const day = formattedVal.getDate()
  const month = formattedVal.getMonth() + 1
  const year = formattedVal.getFullYear()
  const hours = formattedVal.getHours() + 1

  return `${month}/${day}/${year} ${hours}:00`
}

const updateChart = () => {
  chartData.datasets[0].data = formatExpectedDataSet(props.forecastData)
  chartData.datasets[1].data = formatActualDataSet(props.forecastData)
  chartData.datasets[2].data = formatPeakHourDataSet(props.forecastData)
}

const handleDateRangeChange = async (newRange: Array<Date> | null) => {
  if (!newRange) {
    return
  }

  const start = DateTime.fromJSDate(newRange[0]).setZone(RevolveTimezone, { keepLocalTime: true })
  const end = DateTime.fromJSDate(newRange[1]).setZone(RevolveTimezone, { keepLocalTime: true })

  const startISO = start.toISO()
  const endISO = end.toISO()

  if (startISO && endISO) {
    filters.start_date = formatISODateToDateString(startISO)
    filters.end_date = formatISODateToDateString(endISO)
    emit('filtersChange', filters)
  }
}

const handleZoneChange = () => {
  emit('filtersChange', filters)
}

const daysAhead = ref<number>(props.initialDaysAhead)

const handleDaysAheadChange = (value: number) => {
  filters.days_ahead = value === 0 ? undefined : value
  emit('filtersChange', filters)
}

const filters = reactive<ILoadForecastFilters>({
  iso: ISOs.MISO,
  load_resource_zone: 'Z1',
  timezone: 'US/Central',
  start_date: formatISODateToDateString(props.initialStart.toString()),
  end_date: formatISODateToDateString(props.initialEnd.toString()),
  days_ahead: props.initialDaysAhead === 0 ? undefined : props.initialDaysAhead,
})
</script>

<template>
  <div class="load-forecast-chart">
    <el-card v-loading="props.loading">
      <div class="load-forecast-chart__content">
        <div class="load-forecast-chart__header">
          <h3>Load Forecast Chart</h3>
          <div class="load-forecast-chart__controls">
            <div class="daterange">
              <el-date-picker
                v-model="daterange"
                type="daterange"
                start-placeholder="Start date"
                end-placeholder="End date"
                range-separator="to"
                @change="handleDateRangeChange"
              />
            </div>
            <div class="zone">
              <el-select v-model="filters.load_resource_zone" placeholder="Select zone..." @change="handleZoneChange">
                <template #prefix> <Icon icon="material-symbols:my-location-rounded" /> </template>
                <el-option label="Zone 1" value="Z1"></el-option>
                <el-option label="Zone 3" value="Z3"></el-option>
              </el-select>
            </div>
            <div class="days-ahead">
              <el-select v-model="daysAhead" placeholder="Forecast issued..." @change="handleDaysAheadChange">
                <template #prefix> <Icon icon="material-symbols:history-rounded" /> </template>
                <el-option
                  v-for="option in DaysAheadOptions"
                  :key="option.label"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </div>
          </div>
        </div>
        <div class="load-forecast-chart__container" style="min-height: 600px">
          <Chart
            v-if="!props.loading && daterange"
            type="line"
            :options="chartOptions as any"
            :data="chartData"
            :plugins="[Annotation]"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<style scoped lang="scss">
.load-forecast-chart {
  &__content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-lg);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__controls {
    display: inline-flex;
    gap: var(--spacing-spacing-md);

    > .zone,
    > .days-ahead {
      min-width: 128px;
    }
  }
}
</style>
